// src/components/PlayPuzzlePage.js
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import VictoryOverlay from './VictoryOverlay';
import '../utils/puzzleBackground.css';
import SharePuzzle from './SharePuzzle';


const PlayPuzzlePage = () => {
  const { id: puzzleId } = useParams();
  const [image, setImage] = useState('');
  const [message, setMessage] = useState('');
  const [fontStyle, setFontStyle] = useState('serif');
  const [gridSize, setGridSize] = useState(3);
  const [slots, setSlots] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [aspectRatio, setAspectRatio] = useState(1);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isPublic, setIsPublic] = useState(true);

  const canvasWidth = 400;

  useEffect(() => {
    const fetchPuzzle = async () => {
      const ref = doc(db, 'puzzles', puzzleId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setImage(data.image);
        setMessage(data.message);
        setFontStyle(data.fontStyle || 'serif');
        setGridSize(data.gridSize || 3);
        setIsPublic(data.isPublic !== false);
      } else {
        alert('Puzzle not found!');
      }
    };
    fetchPuzzle();
  }, [puzzleId]);

  useEffect(() => {
    if (!image) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = image;

    img.onload = () => {
      try {
        const ratio = img.width / img.height;
        setAspectRatio(ratio);

        const canvasHeight = canvasWidth / ratio;
        const pieceWidth = canvasWidth / gridSize;
        const pieceHeight = canvasHeight / gridSize;

        const canvas = document.createElement('canvas');
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);

        const pieces = [];
        for (let y = 0; y < gridSize; y++) {
          for (let x = 0; x < gridSize; x++) {
            const pieceCanvas = document.createElement('canvas');
            pieceCanvas.width = pieceWidth;
            pieceCanvas.height = pieceHeight;
            const pctx = pieceCanvas.getContext('2d');
            pctx.drawImage(
              canvas,
              x * pieceWidth,
              y * pieceHeight,
              pieceWidth,
              pieceHeight,
              0,
              0,
              pieceWidth,
              pieceHeight
            );
            const dataUrl = pctx.canvas.toDataURL('image/png');
            if (dataUrl.includes('data:image')) {
              pieces.push({ id: y * gridSize + x, img: dataUrl });
            }
          }
        }

        if (!pieces.length) {
          console.error('Failed to generate image pieces – possible CORS issue.');
        }

        const scrambled = [...pieces].sort(() => Math.random() - 0.5);
        setSlots(scrambled);
      } catch (error) {
        console.error('Canvas processing error:', error);
      }
    };
  }, [image, gridSize]);

  const handleSwap = (index) => {
    if (selectedIndex === null) {
      setSelectedIndex(index);
    } else {
      const newSlots = [...slots];
      [newSlots[selectedIndex], newSlots[index]] = [newSlots[index], newSlots[selectedIndex]];
      setSlots(newSlots);
      setSelectedIndex(null);

      if (newSlots.every((p, i) => p.id === i)) {
        setCompleted(true);
      }
    }
  };

  const pieceWidth = canvasWidth / gridSize;
  const pieceHeight = pieceWidth / aspectRatio;

  return (
    <div
      style={{
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '100vh',
      }}
    >
      <div className="play-puzzle-page" style={{ padding: '2rem', textAlign: 'center' }}>
        <div
          className="intro-message"
          style={{
            maxWidth: `${canvasWidth}px`,
            margin: '0 auto 1rem',
            textAlign: 'center',
            wordWrap: 'break-word',
          }}
        >
          Hello, a friend has sent you a private message hidden inside a
          jumbled photograph. Complete the puzzle to receive the secret message.
          Good Luck!
        </div>

        {showPreview && (
          <div style={{ display: 'flex', justifyContent: 'center', margin: '1rem auto' }}>
            <div style={{ position: 'relative' }}>
              <img
                src={image}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  width: `${gridSize * 100}px`,
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  opacity: 0.5,
                  pointerEvents: isPublic ? 'auto' : 'none',
                  userSelect: isPublic ? 'auto' : 'none',
                }}
                draggable={isPublic}
                onContextMenu={(e) => !isPublic && e.preventDefault()}
              />
              {!isPublic && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    pointerEvents: 'none',
                    userSelect: 'none',
                  }}
                >
                  PUZZLE GIFT
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <button
            onClick={() => setShowPreview(!showPreview)}
            style={{ padding: '0.5rem 1rem' }}
          >
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
        </div>

        <div
          style={{
            margin: '1rem auto',
            maxWidth: `${canvasWidth}px`,
            textAlign: 'left',
            wordWrap: 'break-word',
          }}
        >
          <h3 style={{ textAlign: 'center' }}>How to Find the Hidden Message</h3>
          <ol>
            <li>Click/touch a piece. Click/touch another to swap pieces.</li>
            <li>Repeat swapping until the Puzzle is complete.</li>
            <li>Discover the secret hidden message.</li>
          </ol>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="size">Select Puzzle Size: </label>
          <select
            id="size"
            value={gridSize}
            onChange={(e) => setGridSize(Number(e.target.value))}
          >
            {[3, 4, 5, 10].map((n) => (
              <option key={n} value={n}>{n} x {n}</option>
            ))}
          </select>
          {/* <button onClick={() => setGridSize(gridSize)}>Refresh Puzzle</button> */}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${gridSize}, ${pieceWidth}px)`,
            gridTemplateRows: `repeat(${gridSize}, ${pieceHeight}px)`,
            gap: completed ? '0' : '2px',
            border: completed ? 'none' : '2px solid #ccc',
            justifyContent: 'center',
            margin: '0 auto',
            transition: 'all 0.3s ease',
            width: `${pieceWidth * gridSize}px`,
            height: `${pieceHeight * gridSize}px`
          }}
        >
          {slots.map((piece, index) => (
            <div
              key={index}
              onClick={() => handleSwap(index)}
              style={{
                width: pieceWidth,
                height: pieceHeight,
                border: completed ? 'none' : '1px solid #999',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <img
                src={piece.img}
                alt=""
                draggable={isPublic}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  pointerEvents: isPublic ? 'auto' : 'none',
                  userSelect: isPublic ? 'auto' : 'none',
                }}
                onContextMenu={e => !isPublic && e.preventDefault()}
              />
            </div>
          ))}
        </div>
        

        {completed && (
          <>
            <VictoryOverlay />
            <div
              style={{
                position: 'relative',
                width: `${canvasWidth}px`,
                margin: '2rem auto',
              }}
            >
              {!isPublic && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'rgba(200,0,0,0.15)',
                    fontSize: '4rem',
                    fontWeight: 'bold',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    zIndex: 1,
                  }}
                >
                  PUZZLE GIFT
                </div>
              )}
            </div>

            {!isPublic && (
              <div
                style={{
                  fontStyle: 'italic',
                  textAlign: 'center',
                  marginBottom: '1rem',
                  color: '#444',
                }}
              >
                The completed photograph is watermarked at the creator’s request.
              </div>
            )}

            <div
              style={{
                fontFamily: fontStyle,
                fontSize: '1.5rem',
                textAlign: 'center',
                whiteSpace: 'pre-line',
                maxWidth: `${canvasWidth}px`,
                margin: '1rem auto',
                color: '#333',
                wordWrap: 'break-word',
              }}
            >
              {message}
            </div>

            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <a
                href="https://thatpuzzleapp.com/"
                style={{
                  backgroundColor: 'green',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  textDecoration: 'none',
                }}
              >
                Reply
              </a>
              <SharePuzzle puzzleUrl={`https://thatpuzzleapp.com/play/${puzzleId}`} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PlayPuzzlePage;
